import { c as _c } from "react/compiler-runtime";
import "client-only";
import { useMemo, useRef } from "react";
import type { ElementObservation, ElementObservationsAPI } from "@/vendors/Snowplow/types/observation-boundary-tracker.types";
type ElementMap = Map<Element, ElementObservation>;

/**
 * Manages a map of observed elements and their visibility tracking state.
 *
 * Returns a stable API object (via `useMemo`) that provides methods to:
 * - Add/remove elements from tracking
 * - Start/pause visibility timing
 * - Update dimension and pixel metrics
 * - Retrieve all observation data
 *
 * ## Timing approach
 *
 * Uses `performance.now()` for precise duration calculation (monotonic clock,
 * not affected by system time changes) and `Date` for absolute timestamps
 * sent to analytics.
 *
 * ## Why refs instead of state?
 *
 * The element map uses `useRef` instead of `useState` because:
 * - Tracking updates happen frequently (every intersection callback)
 * - These updates don't need to trigger React re-renders
 * - The data is only read when sending to analytics
 *
 */
export function useElementObservations() {
  const $ = _c(11);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Map();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const mapRef = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = (element, observation) => {
      mapRef.current.set(element, observation);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const addElement = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = element_0 => mapRef.current.get(element_0);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const getElement = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => Array.from(mapRef.current.values());
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const getAllElements = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (element_1, now, mark) => {
      const observation_0 = mapRef.current.get(element_1);
      if (!observation_0 || observation_0.isCurrentlyVisible) {
        return;
      }
      mapRef.current.set(element_1, {
        ...observation_0,
        isCurrentlyVisible: true,
        lastViewTimestamp: now,
        viewCount: observation_0.viewCount + 1,
        visibilityStartMark: mark,
        ...(observation_0.viewCount === 0 && {
          firstViewTimestamp: now
        })
      });
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const startTracking = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (element_2, now_0, mark_0) => {
      const observation_1 = mapRef.current.get(element_2);
      if (!observation_1 || !observation_1.isCurrentlyVisible) {
        return;
      }
      const added = observation_1.visibilityStartMark !== null ? mark_0 - observation_1.visibilityStartMark : 0;
      mapRef.current.set(element_2, {
        ...observation_1,
        isCurrentlyVisible: false,
        lastViewTimestamp: now_0,
        timeInViewMs: observation_1.timeInViewMs + added,
        visibilityStartMark: null
      });
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const pauseTracking = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (now_1, mark_1) => {
      for (const [element_3, observation_2] of mapRef.current) {
        if (observation_2.isCurrentlyVisible) {
          pauseTracking(element_3, now_1, mark_1);
        }
      }
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const pauseAllTracking = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (element_4, width, height) => {
      const observation_3 = mapRef.current.get(element_4);
      if (!observation_3) {
        return;
      }
      mapRef.current.set(element_4, {
        ...observation_3,
        maxElementHeight: Math.max(observation_3.maxElementHeight, height),
        maxElementWidth: Math.max(observation_3.maxElementWidth, width)
      });
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const updateDimensions = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (element_5, pixels) => {
      const observation_4 = mapRef.current.get(element_5);
      if (!observation_4) {
        return;
      }
      mapRef.current.set(element_5, {
        ...observation_4,
        maxPixelsInView: Math.max(observation_4.maxPixelsInView, pixels)
      });
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const updateVisiblePixels = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      for (const [element_6, observation_5] of mapRef.current) {
        if (!element_6.isConnected) {
          mapRef.current.delete(element_6);
          continue;
        }
        mapRef.current.set(element_6, {
          content: observation_5.content,
          elementId: observation_5.elementId,
          elementType: observation_5.elementType,
          firstViewTimestamp: null,
          isCurrentlyVisible: false,
          lastViewTimestamp: null,
          maxElementHeight: 0,
          maxElementWidth: 0,
          maxPixelsInView: 0,
          timeInViewMs: 0,
          viewCount: 0,
          visibilityStartMark: null
        });
      }
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const resetAfterSend = t9;
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    const clear = () => {
      mapRef.current.clear();
    };
    t10 = {
      addElement,
      clear,
      getAllElements,
      getElement,
      pauseAllTracking,
      pauseTracking,
      resetAfterSend,
      startTracking,
      updateDimensions,
      updateVisiblePixels
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  return t10;
}